import { convertUnixSecondsToISO } from '../../components/routes-planner/utils';
import type { RoutesPlannerRun, RoutesPlannerTask } from '../../types';
import type { RoutesPlannerBreak } from '../../components/routes-planner/routes-planner';
import type { Directions } from '@bringg/types/types/directions';
import { isLate } from '../../components/routes-planner/timeline-logic/tasks-logic';
import type {
	EtaResponseBreakItem,
	EtaResponseTaskItem,
	EtaResponseRouteItem,
	EtaCalculationUpdateResults
} from '@bringg-frontend/eta-calculator';

export const process = (
	etaCalculationsResult: EtaCalculationUpdateResults[],
	tasksToUpdateByRun: { [runId: number]: RoutesPlannerTask[] },
	routesById: { [runId: number]: RoutesPlannerRun },
	routeInfoByIndex: { [runId: number]: EtaResponseRouteItem },
	consecutiveRoutesById: Map<number, RoutesPlannerRun>,
	consecutiveRoutesTasksById: Map<number, RoutesPlannerTask>
): {
	runToTaskUpdate: { [runId: number]: RoutesPlannerTask[] };
	runToBreaksUpdate: { [runId: number]: RoutesPlannerBreak[] };
	runToDirectionUpdate: { [runId: number]: Directions };
	routeInfoByIndex: { [runId: number]: EtaResponseRouteItem };
	updatedConsecutiveRoutes: EtaResponseRouteItem[] | RoutesPlannerRun[];
	updatedConsecutiveTasks: EtaResponseTaskItem[] | RoutesPlannerTask[];
	updatedConsecutiveBreaks: RoutesPlannerBreak[];
} => {
	const { routesEtaCalculations, consecutiveRoutesEtaCalculations } = splitConsecutiveAndNonConsecutive(
		etaCalculationsResult,
		consecutiveRoutesById,
		consecutiveRoutesTasksById
	);

	const {
		runToTaskUpdate,
		runToBreaksUpdate,
		runToDirectionUpdate,
		routeInfoByIndex: routesInfoByIndex
	} = processRoutes(routesEtaCalculations, tasksToUpdateByRun, routesById, routeInfoByIndex);

	const { updatedConsecutiveRoutes, updatedConsecutiveTasks, updatedConsecutiveBreaks } = processConsecutiveRoutes(
		consecutiveRoutesEtaCalculations,
		tasksToUpdateByRun
	);

	return {
		runToTaskUpdate,
		runToBreaksUpdate,
		runToDirectionUpdate,
		routeInfoByIndex: routesInfoByIndex,
		updatedConsecutiveRoutes,
		updatedConsecutiveTasks,
		updatedConsecutiveBreaks
	};
};

const splitConsecutiveAndNonConsecutive = (
	etaCalculationsResults: EtaCalculationUpdateResults[],
	consecutiveRoutesById: Map<number, RoutesPlannerRun>,
	consecutiveRoutesTasksById: Map<number, RoutesPlannerTask>
) => {
	const consecutiveRoutesEtaCalculations: Pick<
		EtaCalculationUpdateResults,
		'updatedRunsTasks' | 'updatedRoutesInfo' | 'updatedRunsBreaks'
	> = {
		updatedRoutesInfo: [],
		updatedRunsTasks: [],
		updatedRunsBreaks: []
	};
	const routesEtaCalculations: EtaCalculationUpdateResults = {
		updatedRoutesInfo: [],
		updatedRunsBreaks: [],
		updatedRunsTasks: [],
		updatedRunsDirections: []
	};

	const visitedRoutes = {};
	const visitedTasks = {};
	const visitedBreaks = {};
	const visitedDirections = {};

	etaCalculationsResults.forEach(etaCalculations => {
		const { updatedRoutesInfo, updatedRunsBreaks, updatedRunsTasks, updatedRunsDirections } = etaCalculations;
		const changedRoutes = {};

		updatedRoutesInfo.forEach(routeInfo => {
			const etaCalculationResults = consecutiveRoutesById.has(routeInfo.run_id)
				? consecutiveRoutesEtaCalculations
				: routesEtaCalculations;

			if (visitedRoutes[routeInfo.run_id]) {
				if (visitedRoutes[routeInfo.run_id].depot_etl < routeInfo.depot_etl) {
					visitedRoutes[routeInfo.run_id] = routeInfo;
					changedRoutes[routeInfo.run_id] = true;

					const indexOfPrevious = etaCalculationResults.updatedRoutesInfo
						.map(routeInfo => routeInfo.run_id)
						.indexOf(routeInfo.run_id);
					etaCalculationResults.updatedRoutesInfo[indexOfPrevious] = routeInfo;
				}
			} else {
				visitedRoutes[routeInfo.run_id] = routeInfo;
				changedRoutes[routeInfo.run_id] = true;

				etaCalculationResults.updatedRoutesInfo.push(routeInfo);
			}
		});

		updatedRunsTasks.forEach(task => {
			const etaCalculationResults = consecutiveRoutesTasksById.has(task.task_id)
				? consecutiveRoutesEtaCalculations
				: routesEtaCalculations;
			addLatestResultItem(task, ['task_id'], visitedTasks, changedRoutes, etaCalculationResults.updatedRunsTasks);
		});

		updatedRunsBreaks.forEach(runBreak => {
			const etaCalculationResults = consecutiveRoutesById.has(runBreak.run_id)
				? consecutiveRoutesEtaCalculations
				: routesEtaCalculations;
			addLatestResultItem(
				runBreak,
				['run_id', 'position'],
				visitedBreaks,
				changedRoutes,
				etaCalculationResults.updatedRunsBreaks
			);
		});

		updatedRunsDirections.forEach(direction => {
			const isConsecutiveRoute = consecutiveRoutesById.has(direction.run_id);

			if (!isConsecutiveRoute) {
				addLatestResultItem(
					direction,
					['run_id'],
					visitedDirections,
					changedRoutes,
					routesEtaCalculations.updatedRunsDirections
				);
			}
		});
	});

	return {
		consecutiveRoutesEtaCalculations,
		routesEtaCalculations
	};
};

const processConsecutiveRoutes = (
	consecutiveRoutesEtaCalculations: Pick<
		EtaCalculationUpdateResults,
		'updatedRunsTasks' | 'updatedRoutesInfo' | 'updatedRunsBreaks'
	>,
	tasksToUpdateByRun
): {
	updatedConsecutiveRoutes: EtaResponseRouteItem[];
	updatedConsecutiveTasks: RoutesPlannerTask[];
	updatedConsecutiveBreaks: RoutesPlannerBreak[];
} => {
	const { updatedRunsTasks, updatedRoutesInfo, updatedRunsBreaks } = consecutiveRoutesEtaCalculations;

	const updatedConsecutiveTasks = updatedRunsTasks.map(responseTask =>
		getUpdatedTask(responseTask, tasksToUpdateByRun[responseTask.run_id])
	);
	const updatedConsecutiveRoutes = updatedRoutesInfo.map(route => ({
		...route,
		route_idx: route.run_id || route.route_idx
	}));
	const updatedConsecutiveBreaks = updatedRunsBreaks.map(runBreak => updateBreak(runBreak, runBreak.run_id));

	return {
		updatedConsecutiveRoutes,
		updatedConsecutiveTasks,
		updatedConsecutiveBreaks
	};
};

function addLatestResultItem(item, uniqueFields: string[], visitedItems, changedRoutes, etaCalculationResults) {
	const uniqueValues = uniqueFields.map(uniqueField => item[uniqueField]).join('-');

	if (visitedItems[uniqueValues]) {
		if (changedRoutes[item.run_id]) {
			visitedItems[uniqueValues] = item;

			const indexOfPrevious = etaCalculationResults
				.map(resultItem => getUniqueValues(uniqueFields, resultItem))
				.indexOf(uniqueValues);
			etaCalculationResults[indexOfPrevious] = item;
		}
	} else {
		visitedItems[uniqueValues] = item;
		etaCalculationResults.push(item);
	}
}

function getUniqueValues(uniqueFields: string[], item) {
	return uniqueFields.map(uniqueField => item[uniqueField]).join('-');
}

const processRoutes = (
	runsCalculations: EtaCalculationUpdateResults,
	tasksToUpdateByRun: { [runId: number]: RoutesPlannerTask[] },
	routesById: { [runId: number]: RoutesPlannerRun },
	routeInfoById: { [runId: number]: EtaResponseRouteItem }
) => {
	const runToTaskUpdate: { [runId: number]: RoutesPlannerTask[] } = {};
	const runToBreaksUpdate: { [runId: number]: RoutesPlannerBreak[] } = {};
	const runToDirectionUpdate: { [runId: number]: Directions } = {};
	const routeInfoByIndex = {};

	const { updatedRunsTasks, updatedRunsBreaks, updatedRunsDirections, updatedRoutesInfo } = runsCalculations;

	const breaks = updatedRunsBreaks.map(runBreak => updateBreak(runBreak, runBreak.run_id));
	const tasks = updatedRunsTasks.map(responseTask =>
		getUpdatedTask(responseTask, tasksToUpdateByRun[responseTask.run_id])
	);

	tasks.forEach(orderedTask => {
		const runId = orderedTask.run_id;
		if (!runToTaskUpdate[runId]) {
			runToTaskUpdate[runId] = [];
		}
		runToTaskUpdate[runId].push(orderedTask);
	});

	breaks.forEach(orderedBreak => {
		const runId = orderedBreak.run_id;
		if (!runToBreaksUpdate[runId]) {
			runToBreaksUpdate[runId] = [];
		}
		runToBreaksUpdate[runId].push(orderedBreak);
	});

	updatedRunsDirections.forEach(updatedDirection => {
		const runId = updatedDirection.run_id || updatedDirection.route_idx;
		runToDirectionUpdate[runId] = { run_id: runId, directions: updatedDirection.directions };
	});

	updatedRoutesInfo.forEach(routeInfo => {
		const runId = routeInfo.run_id;
		const routeIdx = routeInfoById[runId]?.route_idx || routesById[runId].route_idx;
		routeInfoByIndex[runId] = { ...routeInfo, route_idx: routeIdx };
	});

	return {
		runToTaskUpdate,
		runToBreaksUpdate,
		runToDirectionUpdate,
		routeInfoByIndex
	};
};

const updateBreak = (breakItem: EtaResponseBreakItem, runId: number): RoutesPlannerBreak => {
	return {
		...breakItem,
		run_id: runId,
		scheduled_start_time: convertUnixSecondsToISO(breakItem.scheduled_start_time),
		scheduled_end_time: convertUnixSecondsToISO(breakItem.scheduled_end_time),
		estimated_start_time: convertUnixSecondsToISO(breakItem.estimated_start_time),
		estimated_end_time: convertUnixSecondsToISO(breakItem.estimated_end_time)
	};
};

const getUpdatedTask = (taskItem: EtaResponseTaskItem, tasksOfRuns: RoutesPlannerTask[]): RoutesPlannerTask => {
	const taskToUpdate = tasksOfRuns.find(task => taskItem.task_id === task.id);

	const wp = {
		...taskToUpdate.way_points[0],
		scheduled_at: convertUnixSecondsToISO(taskItem.eta),
		eta: convertUnixSecondsToISO(taskItem.eta),
		etl: convertUnixSecondsToISO(taskItem.etl)
	};

	if (wp.has_to_leave_by) {
		delete wp.has_to_leave_by; // remove from old item that was in priority = 1
	}

	if (taskItem.priority === 1) {
		wp.has_to_leave_by = convertUnixSecondsToISO(taskItem.has_to_leave);
	}

	return {
		...taskToUpdate,
		priority: taskItem.priority,
		way_points: [wp, ...taskToUpdate.way_points.slice(1)],
		late: taskToUpdate.late || isLate(wp)
	};
};

export const etaResponseProcessor = { process };
